import { useEffect, useRef, useState } from 'react';
import { addSongsToPlaylist } from '../api/playlist';
import { fetcher } from './axios';
import { additionOnlySongIds } from './playlistConflictRecovery';

export default function usePlaylistConflictRecovery({ playlist, originalSongIds, draft, metadataChanged, onRecovered }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmedSongIds, setConfirmedSongIds] = useState([]);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const hasDraft = !!draft;
  useEffect(() => {
    setError('');
    setConfirmedSongIds([]);
  }, [playlist.id, hasDraft]);
  const additions = additionOnlySongIds(originalSongIds, draft);
  const canAdd = (playlist.type === 3 || playlist.type === 4) && !metadataChanged && additions?.length > 0;
  const pendingSongIds = (additions || []).filter((id) => !confirmedSongIds.includes(id));
  const reloadOnly = canAdd && pendingSongIds.length === 0;

  const addAndReload = async () => {
    if (!canAdd || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    let saved = reloadOnly;
    try {
      if (pendingSongIds.length) {
        // Once acknowledged, these additions must not be resent if reloading fails.
        await addSongsToPlaylist(playlist.id, pendingSongIds);
        saved = true;
        if (mounted.current) setConfirmedSongIds((current) => [...new Set([...current, ...pendingSongIds])]);
      }
      const playlists = await fetcher('/playlists/');
      const latest = playlists.find((item) => item.id === playlist.id);
      if (!latest) throw new Error('Playlist not found');
      if (mounted.current) onRecovered(latest);
    } catch (err) {
      if (mounted.current) setError(saved
        ? 'Your songs were added, but the latest playlist could not be loaded. Retry reload to view it; the saved additions will not be sent again.'
        : 'Could not add your songs. Your unsaved changes are still here. Please try again.');
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return { canAdd, addAndReload, reloadOnly, busy, error };
}
